"use client";
import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * The currently-rendered CMS page's own slug (in the active language) plus the slug
 * that same Page uses in every other active language — resolved from the
 * PageTranslation relationship server-side (see PublicPageDto.LocalizedSlugs), never
 * hardcoded. `sourceSlug` lets consumers detect stale data left over from a
 * previously-viewed page during client-side navigation (see PageLocaleRegistrar).
 */
type PageLocaleInfo = {
  sourceSlug: string;
  slugs: Record<string, string>;
} | null;

interface PageLocaleContextType {
  pageLocale: PageLocaleInfo;
  setPageLocale: (info: PageLocaleInfo) => void;
}

const PageLocaleContext = createContext<PageLocaleContextType | undefined>(undefined);

export const usePageLocaleContext = () => {
  const context = useContext(PageLocaleContext);
  if (!context) {
    throw new Error("usePageLocaleContext must be used within a PageLocaleProvider");
  }
  return context;
};

export const PageLocaleProvider = ({ children }: { children: React.ReactNode }) => {
  const [pageLocale, setPageLocaleState] = useState<PageLocaleInfo>(null);

  const setPageLocale = useCallback((info: PageLocaleInfo) => {
    setPageLocaleState(info);
  }, []);

  return (
    <PageLocaleContext.Provider value={{ pageLocale, setPageLocale }}>
      {children}
    </PageLocaleContext.Provider>
  );
};
